import React, { useState, useRef, useEffect } from "react";
import { MoreVertical } from "lucide-react";
import { Reorder } from "framer-motion";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { IconLoader2, IconPlus } from "@tabler/icons-react";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface ISidebarSubItem {
  type: "folder" | "tab";
  value: string;
  label: string;
  icon: React.ReactNode;
  id: string | null;
  route?: string;
  children?: ISidebarSubItem[];
}

export interface ISidebarSubItemAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: (itemId: string, newValue?: string) => void;
  className?: string;
  disabled?: boolean;
  renderCustom?: (itemId: string, onClose?: () => void) => React.ReactNode;
}

interface SidebarSubItemProps {
  item: ISidebarSubItem;
  isSelected: boolean;
  isChild?: boolean;
  selectedChildId?: string | null;
  onSelect: (item: ISidebarSubItem) => void;
  parentActions?: ISidebarSubItemAction[];
  childActions?: ISidebarSubItemAction[];
  addChildButtonLabel?: string;
  onAddChild?: () => void;
  hideAddChildButton?: boolean;
  isAddingChild?: boolean;
  isParentDraggable?: boolean;
  isChildDraggable?: boolean;
  isChildLoading?: boolean;
  className?: string;
  // Props for framer-motion reordering
  children?: ISidebarSubItem[];
  setChildren?: (children: ISidebarSubItem[]) => void;
  isBeingDragged?: boolean;
}

// Animation delay to allow drop animation to complete
const DROP_ANIMATION_DELAY = 300;

const SidebarSubItem = ({
  item,
  isSelected,
  selectedChildId,
  isChild,
  onSelect,
  parentActions = [],
  childActions = [],
  addChildButtonLabel = "Add Section",
  onAddChild,
  hideAddChildButton = false,
  isAddingChild = false,
  isParentDraggable = false,
  isChildDraggable = false,
  isChildLoading = false,
  className,
  children,
  setChildren,
  isBeingDragged = false,
}: SidebarSubItemProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [localChildren, setLocalChildren] = useState<ISidebarSubItem[]>(children || []);
  const [isDraggingChild, setIsDraggingChild] = useState(false);
  const [draggingChildId, setDraggingChildId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasChildren = item.children && item.children.length > 0;
  const renameInputRef = useRef<HTMLInputElement>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to clear click timeout
  const clearClickTimeout = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  };

  // Sync local state with prop changes
  useEffect(() => {
    if (!isDraggingChild && children) {
      setLocalChildren(children);
    }
  }, [children, isDraggingChild]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      clearClickTimeout();
    };
  }, []);

  // Focus and select text immediately when renaming starts
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready, then immediately focus and select
      requestAnimationFrame(() => {
        if (renameInputRef.current) {
          renameInputRef.current.focus();
          renameInputRef.current.select();
        }
      });

      const handleClickOutside = (event: MouseEvent) => {
        if (
          renameInputRef.current &&
          !renameInputRef.current.contains(event.target as Node)
        ) {
          finishRenaming();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenaming]);

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearClickTimeout();
    // Select immediately for instant feedback.
    // Double-click rename still works — it activates rename mode after selection.
    onSelect(item);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearClickTimeout();
    const renameAction = parentActions.find((action) => action.id === "rename");
    if (renameAction) setIsRenaming(true);
  };

  const finishRenaming = () => {
    if (renameInputRef.current?.value) {
      const newValue = renameInputRef.current.value.trim();
      // Only call rename API if the name actually changed
      if (newValue !== item.label && newValue.length > 0) {
        const renameAction = parentActions.find((action) => action.id === "rename");
        if (renameAction) {
          renameAction.onClick(item.id || "", newValue);
        }
      }
    }
    setTimeout(() => {
      setIsRenaming(false);
    }, 100);
  };

  // Handle reorder during drag (update local state only)
  const handleReorder = (newOrder: ISidebarSubItem[]) => {
    setLocalChildren(newOrder);
  };

  // Handle drag end (commit changes to parent)
  const handleDragEnd = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Delay state update to allow drop animation to complete smoothly
    timeoutRef.current = setTimeout(() => {
      setIsDraggingChild(false);
      setDraggingChildId(null);
      // Only call setChildren if order actually changed
      if (children && setChildren) {
        const hasChanged = localChildren.some(
          (child, index) => child.id !== children[index]?.id,
        );
        if (hasChanged) {
          setChildren(localChildren);
        }
      }
    }, DROP_ANIMATION_DELAY);
  };

  // Modify the parentActions to handle rename internally
  const modifiedParentActions = parentActions.map((action) => {
    if (action.id === "rename") {
      return {
        ...action,
        onClick: () => setIsRenaming(true),
      };
    }
    return action;
  });

  // Wrapper for onSelect to prevent selection while dragging children
  const handleChildSelect = (item: ISidebarSubItem) => {
    if (isDraggingChild) return;
    onSelect(item);
  };
  return (
    <div
      className={cn(
        "flex flex-col w-full bg-white",
        isParentDraggable ? "cursor-pointer active:cursor-grabbing" : "cursor-pointer",
        isBeingDragged && "cursor-grabbing",
      )}
    >
      <div
        onClick={handleItemClick}
        onDoubleClick={handleDoubleClick}
        className={cn(
          "group relative w-full h-[28px] py-[4px] pl-[16px] pr-[4px] flex items-center rounded-[6px] overflow-visible",
          isSelected && !isChild ? "bg-[#F6F6F6]" : "bg-transparent hover:bg-[#F6F6F699]",
          "[&:has([data-state='open'])]:bg-[#F6F6F6]",
          className,
        )}
      >
        <div className="w-full flex items-center justify-between">
          <div className={cn("flex-1 flex items-center gap-[6px] overflow-hidden")}>
            {isParentDraggable && (
              <div
                className={cn(
                  "absolute left-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center",
                  "cursor-grab active:cursor-grabbing",
                  isBeingDragged && "cursor-grabbing",
                )}
              >
                <DragHandleDots2Icon
                  fontSize={12}
                  className="text-brand-primary-400 flex-shrink-0"
                />
              </div>
            )}
            <div
              className={cn("flex flex-col justify-center items-center rounded-[4px]")}
            >
              <p
                className={cn(
                  "flex items-center justify-center flex-shrink-0 leading-none",
                )}
              >
                {React.cloneElement(item.icon as React.ReactElement, {
                  size: 14,
                  style: {
                    color: isSelected ? "#67749B" : "#9AA3BD",
                  },
                })}
              </p>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden flex items-center">
              {isRenaming ? (
                <div
                  className="w-full flex items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    ref={renameInputRef}
                    defaultValue={item.label}
                    className="w-full h-[24px] p-1 border-0 rounded-[4px] text-xs font-normal text-brand-primary-800 tracking-[0.02em] focus:outline-none shadow-none"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        finishRenaming();
                      } else if (e.key === "Escape") {
                        setIsRenaming(false);
                      }
                    }}
                  />
                </div>
              ) : (
                <Tooltip delayDuration={800}>
                  <TooltipTrigger asChild>
                    <p
                      className={cn(
                        "mt-[1px] truncate text-left text-[12px] font-normal tracking-[0.28px] w-full",
                        isParentDraggable
                          ? "cursor-pointer active:cursor-grabbing"
                          : "cursor-pointer",
                        isBeingDragged && "cursor-grabbing",
                        isSelected
                          ? "text-brand-primary-800 font-medium"
                          : "text-brand-primary-600 font-normal",
                      )}
                    >
                      {item.label}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent align="start" sideOffset={10}>
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          {parentActions.length > 0 && (
            <div className="shrink-0">
              <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <DropdownMenuTrigger
                  asChild
                  className="group-hover:opacity-100 data-[state=open]:opacity-100 opacity-0 shrink-0"
                >
                  <MoreVertical
                    className="h-[14px] w-[14px] stroke-[1.5] ml-auto text-brand-primary-400 cursor-pointer shrink-0"
                    size={14}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  side="right"
                  className="p-1 border-none rounded-[12px] squircle squircle-smooth-[1] squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white"
                >
                  {modifiedParentActions.map((action) => {
                    if (action.renderCustom) {
                      return (
                        <div key={action.id} className="w-full">
                          {action.renderCustom(item.id || "", () =>
                            setIsDropdownOpen(false),
                          )}
                        </div>
                      );
                    }
                    return (
                      <DropdownMenuItem
                        key={action.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (action.id === "rename") {
                            action.onClick(item.id || "");
                          } else {
                            action.onClick(item.id || "");
                          }
                        }}
                        disabled={action.disabled}
                        className={cn(
                          "cursor-pointer h-7 px-2 py-1 border-0 rounded-[8px] hover:bg-hover-gray aria-selected:bg-transparent flex items-center gap-[6px] text-brand-primary-800 hover:text-brand-primary-800",
                          action.className,
                          action.disabled && "opacity-50 cursor-not-allowed",
                        )}
                      >
                        {action.icon}
                        <span className="h-[14px] text-[12px]">{action.label}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
      {(hasChildren || isChildLoading) && (
        <div className="relative ml-[20px] mt-[8px] flex flex-col gap-[2px]">
          <div className="absolute top-0 -left-[6px] w-[13px] h-full border-l-[1px] border-[#EDEDED]"></div>
          {isChildLoading ? (
            <>
              {Array.from({ length: 1 }).map((_, index) => (
                <div className="relative" key={index}>
                  <div key={index} className="flex items-center gap-2">
                    <Skeleton className="h-[28px] flex-1 rounded-[8px] bg-[#F6F6F6]" />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {isChildDraggable && children && setChildren ? (
                <Reorder.Group
                  axis="y"
                  values={localChildren}
                  onReorder={handleReorder}
                  className="flex flex-col gap-[2px]"
                >
                  {localChildren.map((child) => (
                    <Reorder.Item
                      key={child.id}
                      value={child}
                      className="relative"
                      onDragStart={() => {
                        setIsDraggingChild(true);
                        setDraggingChildId(child.id || null);
                      }}
                      onDragEnd={handleDragEnd}
                      transition={{
                        layout: isDraggingChild
                          ? { type: "spring", stiffness: 400, damping: 40 }
                          : { duration: 0 },
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        y: { type: "spring", stiffness: 300, damping: 30 },
                      }}
                    >
                      <div className="w-full rounded-[4px] cursor-pointer overflow-hidden">
                        <SidebarSubItem
                          item={child}
                          isSelected={selectedChildId === child.id}
                          onSelect={handleChildSelect}
                          parentActions={childActions}
                          isParentDraggable={isChildDraggable}
                          isChildDraggable={false}
                          isChild={true}
                          isBeingDragged={draggingChildId === child.id}
                        />
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : (
                <>
                  {item.children?.map((child) => (
                    <div className="relative" key={child.id}>
                      <div className="w-full rounded-[4px] cursor-pointer overflow-hidden">
                        <SidebarSubItem
                          item={child}
                          isSelected={selectedChildId === child.id}
                          onSelect={onSelect}
                          parentActions={childActions}
                          isParentDraggable={isChildDraggable}
                          isChildDraggable={false}
                          isChild={true}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
          {onAddChild && !hideAddChildButton && (
            <Button
              variant="ghost"
              className="w-full h-[32px] px-[12px] flex justify-start items-center gap-[6px] hover:bg-[#F6F6F699]"
              onClick={(e) => {
                e.stopPropagation();
                onAddChild();
              }}
            >
              <div className="flex items-center justify-center rounded-[4px]">
                <div className="flex-shrink-0 text-brand-primary-400">
                  <IconPlus size={12} />
                </div>
              </div>
              <span className="overflow-hidden text-brand-primary-600 text-[12px] h-[18px] font-normal tracking-[0.26px]">
                {addChildButtonLabel}
              </span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarSubItem;
